import { ChartSkeleton } from "components/charts/ChartSkeleton";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  PreHeader,
  Spinner,
  Table,
  Text,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useMemo, useState } from "react";
import { MdDownload } from "react-icons/md";
import { dashboardService, SkillUsageStats } from "services/dashboardService";

interface AllSkillsTableCardProps {
  startDate: string;
  endDate: string;
  /** Bumping this re-fetches (wired to the page Refresh button). */
  reloadKey?: number;
  /** Date-only labels used to name the exported CSV file. */
  fromLabel: string;
  toLabel: string;
}

const PAGE_SIZE = 10;

/** Triggers a browser download of a Blob. */
function triggerBrowserDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * All-skills usage table (visible to every user): the uncapped, usage-descending skill list,
 * paginated 10/page, with a CSV export of the full list (not just the current page).
 */
export const AllSkillsTableCard = ({
  startDate,
  endDate,
  reloadKey,
  fromLabel,
  toLabel,
}: AllSkillsTableCardProps) => {
  const { t } = useTranslation();
  const [skills, setSkills] = useState<SkillUsageStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    dashboardService
      .getAllSkillsUsage(7, { startDate, endDate })
      .then((res) => {
        setSkills(res.skills ?? []);
        setPage(1);
        setLoaded(true);
      })
      .catch((error) =>
        console.error("Failed to load all skills usage table", error),
      )
      .finally(() => setLoading(false));
  }, [startDate, endDate, reloadKey]);

  const totalPages = Math.max(1, Math.ceil(skills.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => skills.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [skills, page],
  );

  const colName = t("app.dashboard.skillsTable.skill");
  const colSource = t("app.dashboard.skillsTable.source");
  const colUses = t("app.dashboard.skillsTable.uses");

  const exportCsv = () => {
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [[colName, colSource, colUses].map(esc).join(",")];
    skills.forEach((s) =>
      lines.push([esc(s.skillName), esc(s.source), s.usageCount].join(",")),
    );
    // BOM so Excel opens UTF-8 skill names correctly.
    const blob = new Blob(["﻿" + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    triggerBrowserDownload(blob, `skills-usage_${fromLabel}_${toLabel}.csv`);
  };

  return (
    <Card.Root h="full">
      <Card.Header pb={2}>
        <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
          <Box>
            <PreHeader size="xs">
              {t("app.dashboard.preHeaderSkills")}
            </PreHeader>
            <HStack mt={2} gap={2}>
              <Heading size="md">
                {t("app.dashboard.skillsTable.title")}
              </Heading>
              {loading && loaded && <Spinner size="sm" />}
            </HStack>
          </Box>
          <Button
            intent="secondary"
            mono
            onClick={exportCsv}
            disabled={!loaded || skills.length === 0}
          >
            <MdDownload />
            {t("app.dashboard.skillsTable.exportCsv")}
          </Button>
        </Flex>
      </Card.Header>
      <Card.Body>
        {!loaded ? (
          <ChartSkeleton height={300} />
        ) : skills.length > 0 ? (
          <>
            <Box overflowX="auto">
              <Table.Root size="sm" minW="480px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>{colName}</Table.ColumnHeader>
                    <Table.ColumnHeader>{colSource}</Table.ColumnHeader>
                    <Table.ColumnHeader>{colUses}</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {pageItems.map((s) => (
                    <Table.Row key={s.skillId}>
                      <Table.Cell>{s.skillName}</Table.Cell>
                      <Table.Cell>
                        <Badge
                          colorPalette={
                            s.source === "tenant" ? "blue" : "purple"
                          }
                        >
                          {s.source === "tenant"
                            ? t("app.dashboard.skillsTable.sourceTenant")
                            : t("app.dashboard.skillsTable.sourceCc")}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell
                        style={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {s.usageCount}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
            <Flex
              justify="space-between"
              align="center"
              mt={3}
              wrap="wrap"
              gap={2}
            >
              <Text fontSize="xs" color="var(--text-secondary-color)">
                {t("app.dashboard.skillsTable.totalSkills", {
                  count: skills.length,
                })}
              </Text>
              <HStack gap={2}>
                <Button
                  intent="ghost"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  {t("app.common.previous")}
                </Button>
                <Text
                  fontSize="sm"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {t("app.dashboard.skillsTable.pageOf", {
                    page,
                    total: totalPages,
                  })}
                </Text>
                <Button
                  intent="ghost"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  {t("app.common.next")}
                </Button>
              </HStack>
            </Flex>
          </>
        ) : (
          <Text color="var(--text-secondary-color)" textAlign="center" py={10}>
            {t("app.dashboard.noData")}
          </Text>
        )}
      </Card.Body>
    </Card.Root>
  );
};

export default AllSkillsTableCard;
